import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { generateMatchRosterPDF } from '../utils/pdfRoster';
import { useTableSort } from '../hooks/useTableSort';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Match {
  id: string;
  leagueId: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  fieldId?: string;
  status: string;
  refereeId?: string;
  homeScore?: number;
  awayScore?: number;
  homeTeam?: { id: string; name: string };
  awayTeam?: { id: string; name: string };
  league?: { id: string; name: string };
  referee?: { id: string; firstName: string; lastName: string };
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
}

interface League {
  id: string;
  name: string;
}

interface TeamOption {
  id: string;
  name: string;
}

const Matches: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [matches, setMatches] = useState<Match[]>([]);
  const [myMatches, setMyMatches] = useState<Match[]>([]);
  const [refereeMatches, setRefereeMatches] = useState<Match[]>([]);
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  const [filter, setFilter] = useState<string>(searchParams.get('status') || 'scheduled');
  const [leagueFilter, setLeagueFilter] = useState<string>(searchParams.get('leagueId') || 'all');
  const [teamFilter, setTeamFilter] = useState<string>(searchParams.get('teamId') || 'all');
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>(
    window.innerWidth <= 768 ? 'calendar' : 'grid'
  );
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
  const [referees, setReferees] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [assigningReferee, setAssigningReferee] = useState<string | null>(null);
  const [selectedRefereeId, setSelectedRefereeId] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');
  const [fieldId, setFieldId] = useState<string | null>(null);
  const [allLeagues, setAllLeagues] = useState<League[]>([]);
  const [allTeamOptions, setAllTeamOptions] = useState<TeamOption[]>([]);
  const [myRsvps, setMyRsvps] = useState<Record<string, string>>({});
  const [rsvpSubmitting, setRsvpSubmitting] = useState<string | null>(null);

  // Fetch leagues and teams for filter dropdowns (once on mount)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [leaguesRes, teamsRes] = await Promise.all([
          api.get('/leagues', { params: { page: '1', limit: '200' } }),
          api.get('/teams', { params: { page: '1', limit: '200' } })
        ]);
        const leagues = (leaguesRes.data.leagues || leaguesRes.data || [])
          .map((l: any) => ({ id: l.id, name: l.name }))
          .sort((a: League, b: League) => a.name.localeCompare(b.name));
        setAllLeagues(leagues);

        const teams = (teamsRes.data.teams || teamsRes.data || [])
          .map((t: any) => ({ id: t.id, name: t.name }))
          .sort((a: TeamOption, b: TeamOption) => a.name.localeCompare(b.name));
        setAllTeamOptions(teams);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (isAdminOrSuperAdmin(user?.profileType)) {
      fetchReferees();
    }
  }, [user]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const params: Record<string, string> = {
          page: currentPage.toString(),
          limit: '100'
        };
        if (filter !== 'all') params.status = filter;
        if (leagueFilter !== 'all') params.leagueId = leagueFilter;
        if (teamFilter !== 'all') params.teamId = teamFilter;

        const response = await api.get('/matches', { params });
        const allMatches = response.data.matches || response.data;

        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }

        // Build league map from allLeagues to avoid N+1 fetches
        const leagueMap: Record<string, string> = {};
        allLeagues.forEach(l => { leagueMap[l.id] = l.name; });

        const matchesWithLeagues = allMatches.map((match: Match) => ({
          ...match,
          league: match.leagueId && leagueMap[match.leagueId]
            ? { id: match.leagueId, name: leagueMap[match.leagueId] }
            : match.league || null
        }));

        setMatches(matchesWithLeagues);

        // Get user's teams and their matches
        if (user) {
          try {
            // Get all teams (need to fetch all pages to get user's teams)
            const teamsRes = await api.get('/teams', { params: { page: '1', limit: '100' } });
            const allTeams = teamsRes.data.teams || teamsRes.data;

            // Find teams where user is associated
            const userTeamIds = new Set<string>();
            const userTeamList: Team[] = [];

            // Check teams where user is manager
            allTeams.forEach((team: any) => {
              if (team.manager?.id === user.id) {
                userTeamIds.add(team.id);
                userTeamList.push({ id: team.id, name: team.name, teamNumber: team.teamNumber });
              }
            });

            // Check teams where user is a member
            for (const team of allTeams) {
              try {
                const membersRes = await api.get(`/teams/${team.id}/members`);
                const isMember = membersRes.data.some((m: any) => m.userId === user.id);
                if (isMember && !userTeamIds.has(team.id)) {
                  userTeamIds.add(team.id);
                  userTeamList.push({ id: team.id, name: team.name, teamNumber: team.teamNumber });
                }
              } catch {
                // Skip if can't fetch members
              }
            }

            setUserTeams(userTeamList);

            // Filter matches where user's teams are playing
            const playerMatches = matchesWithLeagues.filter((match: Match) =>
              userTeamIds.has(match.homeTeamId) || userTeamIds.has(match.awayTeamId)
            );

            setMyMatches(playerMatches);
          } catch (error) {
            console.error('Error fetching user teams:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [currentPage, user, filter, leagueFilter, teamFilter, allLeagues]);
  
  // Batch-fetch RSVP statuses for my matches
  useEffect(() => {
    const scheduledMyMatches = myMatches.filter(m => m.status === 'scheduled');
    if (scheduledMyMatches.length === 0) return;

    const fetchMyRsvps = async () => {
      try {
        const matchIds = scheduledMyMatches.map(m => m.id).join(',');
        const response = await api.get(`/matches/my-rsvps?matchIds=${matchIds}`);
        setMyRsvps(response.data);
      } catch (error) {
        console.error('Error fetching RSVPs:', error);
      }
    };

    fetchMyRsvps();
  }, [myMatches]);

  const handleQuickRsvp = async (matchId: string, status: string) => {
    if (rsvpSubmitting) return;
    setRsvpSubmitting(matchId);
    try {
      await api.post(`/matches/${matchId}/rsvp`, { status });
      setMyRsvps(prev => ({ ...prev, [matchId]: status }));
    } catch (error: any) {
      console.error('Error submitting RSVP:', error);
      alert(error.response?.data?.error || 'Failed to submit RSVP');
    } finally {
      setRsvpSubmitting(null);
    }
  };

  const getRsvpBadge = (matchId: string) => {
    const status = myRsvps[matchId];
    if (!status) return <span className="badge" style={{ backgroundColor: '#9E9E9E', color: '#fff' }}>No RSVP</span>;
    if (status === 'attending') return <span className="badge" style={{ backgroundColor: '#2E7D32', color: '#fff' }}>Going</span>;
    if (status === 'maybe') return <span className="badge" style={{ backgroundColor: '#F9A825', color: '#fff' }}>Maybe</span>;
    return <span className="badge" style={{ backgroundColor: '#C62828', color: '#fff' }}>Not Going</span>;
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to sort matches chronologically
  const sortMatchesChronologically = (matches: Match[]): Match[] => {
    return [...matches].sort((a, b) => {
      // First sort by date
      const dateA = new Date(a.scheduledDate).getTime();
      const dateB = new Date(b.scheduledDate).getTime();
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      // If same date, sort by time
      const timeA = a.scheduledTime || '00:00';
      const timeB = b.scheduledTime || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // Filter my matches by team if selected
  const filteredMyMatches = selectedTeamFilter === 'all'
    ? myMatches
    : myMatches.filter(m => m.homeTeamId === selectedTeamFilter || m.awayTeamId === selectedTeamFilter);

  // Apply status filter to my matches and sort chronologically
  const filteredMyMatchesByStatus = sortMatchesChronologically(
    filter === 'all'
      ? filteredMyMatches
      : filteredMyMatches.filter(m => m.status === filter)
  );

  // Filter all matches by status and sort chronologically
  const filteredMatches = sortMatchesChronologically(
    filter === 'all' 
      ? matches 
      : matches.filter(m => m.status === filter)
  );

  // Prepare matches data for sorting
  const matchesWithSortData = filteredMatches.map(match => ({
    ...match,
    sortDate: new Date(match.scheduledDate).getTime(),
    sortHomeTeam: match.homeTeam?.name || '',
    sortAwayTeam: match.awayTeam?.name || '',
    sortLeague: match.league?.name || '',
    sortLocation: match.location || ''
  }));
  
  const { sortedData: sortedMatches, handleSort: handleMatchesSort, getSortIcon: getMatchesSortIcon } = useTableSort(matchesWithSortData, { key: 'sortDate', direction: 'asc' });
  
  const fetchReferees = async (leagueId?: string) => {
    try {
      const response = await api.get('/users');
      const allUsers = response.data.users || response.data || [];
      const refereeUsers = allUsers.filter((u: any) => u.profileType === 'referee');
      
      // Filter referees by league if leagueId is provided
      let filteredReferees = refereeUsers;
      if (leagueId) {
        filteredReferees = refereeUsers.filter((ref: any) => {
          // If allowedLeagueIds is undefined or empty, referee can ref all leagues (backward compatibility)
          if (!ref.allowedLeagueIds || ref.allowedLeagueIds.length === 0) {
            return true;
          }
          // Otherwise, check if league is in allowed list
          return ref.allowedLeagueIds.includes(leagueId);
        });
      }
      
      setReferees(filteredReferees.map((u: any) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName })));
    } catch (error) {
      console.error('Error fetching referees:', error);
    }
  };

  const handleAssignReferee = async (matchId: string) => {
    try {
      await api.patch(`/matches/${matchId}/assign-referee`, {
        refereeId: selectedRefereeId || null
      });
      // Refresh matches
      window.location.reload();
    } catch (error: any) {
      console.error('Error assigning referee:', error);
      alert(error.response?.data?.error || 'Failed to assign referee');
    } finally {
      setAssigningReferee(null);
      setSelectedRefereeId('');
    }
  };

  const handleDownloadRoster = async (matchId: string, teamType: 'home' | 'away') => {
    try {
      const response = await api.get(`/matches/${matchId}/roster`);
      const { match, homeTeamPlayers, awayTeamPlayers } = response.data;
      
      await generateMatchRosterPDF(
        {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          scheduledDate: match.scheduledDate,
          scheduledTime: match.scheduledTime,
          location: match.location,
          league: match.league
        },
        homeTeamPlayers,
        awayTeamPlayers,
        teamType
      );
    } catch (error: any) {
      console.error('Error generating roster PDF:', error);
      alert(error.response?.data?.error || 'Failed to generate roster PDF');
    }
  };

  const handleLocationClick = async (location: string, matchId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedLocation(location);
    setSelectedMatchId(matchId);
    setShowLocationModal(true);

    // Try to find field - first check if match has fieldId
    try {
      const matchRes = await api.get(`/matches/${matchId}`);
      
      // Use fieldId directly from match if available
      if (matchRes.data.fieldId) {
        setFieldId(matchRes.data.fieldId);
        return;
      }
      
      // Fallback to searching by location if fieldId not present
      const fieldsRes = await api.get('/fields', { params: { page: '1', limit: '100' } });
      const fields = fieldsRes.data.fields || fieldsRes.data || [];
      
      const field = fields.find((f: any) => 
        f.name.toLowerCase() === location.toLowerCase() ||
        f.address?.toLowerCase().includes(location.toLowerCase())
      );

      if (field) {
        setFieldId(field.id);
      } else {
        setFieldId(null);
      }
    } catch (error) {
      console.error('Error finding field:', error);
      setFieldId(null);
    }
  };

  const handleGoogleMaps = () => {
    if (!selectedLocation) return;
    const query = encodeURIComponent(selectedLocation);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    setShowLocationModal(false);
  };

  const handleFieldView = () => {
    if (fieldId) {
      navigate(`/fields/${fieldId}`);
    } else {
      alert('Field information not available for this location');
    }
    setShowLocationModal(false);
  };

  // Group matches by date for calendar view (already sorted chronologically)
  const matchesByDate = filteredMatches.reduce((acc, match) => {
    const date = match.scheduledDate;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(match);
    return acc;
  }, {} as Record<string, Match[]>);
  
  // Sort dates chronologically
  const sortedDates = Object.keys(matchesByDate).sort((a, b) => {
    return new Date(a).getTime() - new Date(b).getTime();
  });
  
  // Sort matches within each date by time
  sortedDates.forEach(date => {
    matchesByDate[date].sort((a, b) => {
      const timeA = a.scheduledTime || '00:00';
      const timeB = b.scheduledTime || '00:00';
      return timeA.localeCompare(timeB);
    });
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'scheduled':
        return <span className="badge badge-info">Scheduled</span>;
      case 'in_progress':
        return <span className="badge badge-warning">In Progress</span>;
      case 'cancelled':
        return <span className="badge badge-danger">Cancelled</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
        <h1>Matches</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/calendar/my-matches`, '_blank')}
            style={{ fontSize: '14px', padding: '8px 15px' }}
          >
            Export Calendar
          </button>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span>View:</span>
            <button
              className={viewMode === 'grid' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setViewMode('grid')}
              style={{ fontSize: '14px', padding: '8px 15px' }}
            >
              Grid View
            </button>
            <button
              className={viewMode === 'calendar' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setViewMode('calendar')}
              style={{ fontSize: '14px', padding: '8px 15px' }}
            >
              Calendar View
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <label>Status:</label>
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}
            style={{ padding: '10px', borderRadius: '8px', border: '2px solid #E0E0E0', fontSize: '14px' }}
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <label>League:</label>
          <select
            value={leagueFilter}
            onChange={(e) => { setLeagueFilter(e.target.value); setCurrentPage(1); }}
            style={{ padding: '10px', borderRadius: '8px', border: '2px solid #E0E0E0', fontSize: '14px', minWidth: '160px' }}
          >
            <option value="all">All Leagues</option>
            {allLeagues.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <label>Team:</label>
          <select
            value={teamFilter}
            onChange={(e) => { setTeamFilter(e.target.value); setCurrentPage(1); }}
            style={{ padding: '10px', borderRadius: '8px', border: '2px solid #E0E0E0', fontSize: '14px', minWidth: '160px' }}
          >
            <option value="all">All Teams</option>
            {allTeamOptions.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        {(leagueFilter !== 'all' || teamFilter !== 'all' || filter !== 'scheduled') && (
          <button
            className="btn btn-secondary"
            onClick={() => { setFilter('scheduled'); setLeagueFilter('all'); setTeamFilter('all'); setCurrentPage(1); }}
            style={{ fontSize: '14px', padding: '10px 15px' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Referee Matches Section */}
      {user?.profileType === 'referee' && refereeMatches.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h2>My Assigned Matches</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Matches assigned to you as a referee
          </p>

          <div className="card">
            <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Home Team</th>
                  <th>Score</th>
                  <th>Away Team</th>
                  <th>League</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {refereeMatches
                  .filter((match: Match) => filter === 'all' || match.status === filter)
                  .map((match: Match) => (
                    <tr key={match.id} style={{ backgroundColor: '#E8F5E9' }}>
                      <td>{new Date(match.scheduledDate).toLocaleDateString()}</td>
                      <td>{match.scheduledTime}</td>
                      <td><strong>{match.homeTeam?.name || 'TBD'}</strong></td>
                      <td>
                        {match.status === 'completed' && match.homeScore !== undefined && match.awayScore !== undefined
                          ? `${match.homeScore} - ${match.awayScore}`
                          : '-'}
                      </td>
                      <td><strong>{match.awayTeam?.name || 'TBD'}</strong></td>
                      <td>{match.league?.name || '-'}</td>
                      <td>{match.location}</td>
                      <td>{getStatusBadge(match.status)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {match.status === 'scheduled' ? (
                            <Link
                              to={`/referees/matches/${match.id}/complete`}
                              className="btn btn-sm btn-primary"
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              Complete
                            </Link>
                          ) : (
                            <Link
                              to={`/referees/matches/${match.id}`}
                              className="btn btn-sm btn-secondary"
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </Link>
                          )}
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadRoster(match.id, 'home');
                            }}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                            title="Download Home Team Roster"
                          >
                            📄 Home
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadRoster(match.id, 'away');
                            }}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                            title="Download Away Team Roster"
                          >
                            📄 Away
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* My Matches Section (for players/managers) */}
      {myMatches.length > 0 && user?.profileType !== 'referee' && (
        <div style={{ marginBottom: '30px' }}>
          <h2>My Matches</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Matches for teams you are associated with
          </p>
          
          {userTeams.length > 1 && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ marginRight: '10px' }}>Filter by Team:</label>
              <select
                value={selectedTeamFilter}
                onChange={(e) => setSelectedTeamFilter(e.target.value)}
                style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0', minWidth: '200px' }}
              >
                <option value="all">All My Teams</option>
                {userTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="card">
            <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Home Team</th>
                  <th>Score</th>
                  <th>Away Team</th>
                  <th>League</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>RSVP</th>
                  {isAdminOrSuperAdmin(user?.profileType) && <th>Referee</th>}
                  {isAdminOrSuperAdmin(user?.profileType) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredMyMatchesByStatus.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                      No matches found
                    </td>
                  </tr>
                ) : (
                  filteredMyMatchesByStatus.map((match) => {
                    const isHomeTeam = userTeams.some(t => t.id === match.homeTeamId);
                    const userTeam = userTeams.find(t => t.id === match.homeTeamId || t.id === match.awayTeamId);
                    return (
                      <tr key={match.id} className="clickable" style={{ backgroundColor: '#F5F5F5' }} onClick={() => navigate(`/matches/${match.id}`)}>
                        <td>{new Date(match.scheduledDate).toLocaleDateString()}</td>
                        <td>{match.scheduledTime}</td>
                        <td>
                          <strong style={{ color: isHomeTeam ? '#2E7D32' : 'inherit' }}>
                            {match.homeTeam?.name || 'TBD'}
                          </strong>
                        </td>
                        <td>
                          {match.status === 'completed' ? (
                            <strong>{match.homeScore} - {match.awayScore}</strong>
                          ) : (
                            'vs'
                          )}
                        </td>
                        <td>
                          <strong style={{ color: !isHomeTeam ? '#2E7D32' : 'inherit' }}>
                            {match.awayTeam?.name || 'TBD'}
                          </strong>
                        </td>
                        <td>{match.league?.name || '-'}</td>
                        <td>
                          <span
                            style={{
                              color: '#2E7D32',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontWeight: '500'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLocationClick(match.location, match.id);
                            }}
                          >
                            {match.location}
                          </span>
                        </td>
                        <td>{getStatusBadge(match.status)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {match.status === 'scheduled' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                              {getRsvpBadge(match.id)}
                              <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleQuickRsvp(match.id, 'attending')}
                                  disabled={rsvpSubmitting === match.id}
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    backgroundColor: myRsvps[match.id] === 'attending' ? '#2E7D32' : '#E8F5E9',
                                    color: myRsvps[match.id] === 'attending' ? '#fff' : '#2E7D32',
                                    border: '1px solid #2E7D32',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Yes
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleQuickRsvp(match.id, 'maybe')}
                                  disabled={rsvpSubmitting === match.id}
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    backgroundColor: myRsvps[match.id] === 'maybe' ? '#F9A825' : '#FFF8E1',
                                    color: myRsvps[match.id] === 'maybe' ? '#fff' : '#F9A825',
                                    border: '1px solid #F9A825',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Maybe
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleQuickRsvp(match.id, 'not_attending')}
                                  disabled={rsvpSubmitting === match.id}
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    backgroundColor: myRsvps[match.id] === 'not_attending' ? '#C62828' : '#FFEBEE',
                                    color: myRsvps[match.id] === 'not_attending' ? '#fff' : '#C62828',
                                    border: '1px solid #C62828',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  No
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9E9E9E' }}>-</span>
                          )}
                        </td>
                        {isAdminOrSuperAdmin(user?.profileType) && (
                          <td onClick={(e) => e.stopPropagation()}>
                            {match.referee ? (
                            <Link 
                              to={`/users/${match.referee.id}`} 
                              style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {match.referee.firstName} {match.referee.lastName} (referee)
                            </Link>
                          ) : (
                            'Unassigned'
                          )}
                          </td>
                        )}
                        {(isAdminOrSuperAdmin(user?.profileType) || user?.profileType === 'manager' || user?.profileType === 'referee') && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {isAdminOrSuperAdmin(user?.profileType) && (
                                <>
                                  {assigningReferee === match.id ? (
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                      <select
                                        value={selectedRefereeId}
                                        onChange={(e) => setSelectedRefereeId(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ padding: '4px', fontSize: '12px' }}
                                      >
                                        <option value="">Unassign</option>
                                        {referees.map(ref => (
                                          <option key={ref.id} value={ref.id}>
                                            {ref.firstName} {ref.lastName}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAssignReferee(match.id);
                                        }}
                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAssigningReferee(null);
                                          setSelectedRefereeId('');
                                        }}
                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setAssigningReferee(match.id);
                                        setSelectedRefereeId(match.refereeId || '');
                                        // Fetch referees filtered by this match's league
                                        await fetchReferees(match.leagueId);
                                      }}
                                      style={{ fontSize: '12px', padding: '4px 8px' }}
                                    >
                                      {match.refereeId ? 'Change Ref' : 'Assign Ref'}
                                    </button>
                                  )}
                                </>
                              )}
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadRoster(match.id, 'home');
                                }}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                title="Download Home Team Roster PDF"
                              >
                                📄 Home
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadRoster(match.id, 'away');
                                }}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                title="Download Away Team Roster PDF"
                              >
                                📄 Away
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* All Matches Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>All Matches ({pagination.total > 0 ? pagination.total : filteredMatches.length} matches)</h2>
        {pagination.totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {viewMode === 'grid' ? (
        <div className="card">
          <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('sortDate' as any)}>
                  Date {getMatchesSortIcon('sortDate' as any)}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('scheduledTime' as any)}>
                  Time {getMatchesSortIcon('scheduledTime' as any)}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('sortHomeTeam' as any)}>
                  Home Team {getMatchesSortIcon('sortHomeTeam' as any)}
                </th>
                <th>Score</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('sortAwayTeam' as any)}>
                  Away Team {getMatchesSortIcon('sortAwayTeam' as any)}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('sortLeague' as any)}>
                  League {getMatchesSortIcon('sortLeague' as any)}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('sortLocation' as any)}>
                  Location {getMatchesSortIcon('sortLocation' as any)}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleMatchesSort('status' as any)}>
                  Status {getMatchesSortIcon('status' as any)}
                </th>
                {isAdminOrSuperAdmin(user?.profileType) && <th>Referee</th>}
                {(isAdminOrSuperAdmin(user?.profileType) || user?.profileType === 'manager' || user?.profileType === 'referee') && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedMatches.length === 0 ? (
                <tr>
                  <td colSpan={
                    (isAdminOrSuperAdmin(user?.profileType) ? 2 : 0) + 
                    ((isAdminOrSuperAdmin(user?.profileType) || user?.profileType === 'manager' || user?.profileType === 'referee') ? 1 : 0) + 
                    8
                  } style={{ textAlign: 'center', padding: '20px' }}>
                    No matches found
                  </td>
                </tr>
              ) : (
                sortedMatches.map((match) => (
                    <tr key={match.id} className="clickable" onClick={() => navigate(`/matches/${match.id}`)}>
                      <td>{new Date(match.scheduledDate).toLocaleDateString()}</td>
                      <td>{match.scheduledTime}</td>
                      <td><strong>{match.homeTeam?.name || 'TBD'}</strong></td>
                      <td>
                        {match.status === 'completed' ? (
                          <strong>{match.homeScore} - {match.awayScore}</strong>
                        ) : (
                          'vs'
                        )}
                      </td>
                      <td><strong>{match.awayTeam?.name || 'TBD'}</strong></td>
                      <td>{match.league?.name || '-'}</td>
                      <td>
                        <span
                          style={{ 
                            color: '#2E7D32', 
                            cursor: 'pointer', 
                            textDecoration: 'underline',
                            fontWeight: '500'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLocationClick(match.location, match.id);
                          }}
                        >
                          {match.location}
                        </span>
                      </td>
                      <td>{getStatusBadge(match.status)}</td>
                      {isAdminOrSuperAdmin(user?.profileType) && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {match.referee ? (
                            <Link 
                              to={`/users/${match.referee.id}`} 
                              style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {match.referee.firstName} {match.referee.lastName} (referee)
                            </Link>
                          ) : (
                            'Unassigned'
                          )}
                        </td>
                      )}
                      {(isAdminOrSuperAdmin(user?.profileType) || user?.profileType === 'manager' || user?.profileType === 'referee') && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            {isAdminOrSuperAdmin(user?.profileType) && (
                              <>
                                {assigningReferee === match.id ? (
                                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <select
                                      value={selectedRefereeId}
                                      onChange={(e) => setSelectedRefereeId(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ padding: '4px', fontSize: '12px' }}
                                    >
                                      <option value="">Unassign</option>
                                      {referees.map(ref => (
                                        <option key={ref.id} value={ref.id}>
                                          {ref.firstName} {ref.lastName}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAssignReferee(match.id);
                                      }}
                                      style={{ fontSize: '12px', padding: '4px 8px' }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAssigningReferee(null);
                                        setSelectedRefereeId('');
                                      }}
                                      style={{ fontSize: '12px', padding: '4px 8px' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setAssigningReferee(match.id);
                                      setSelectedRefereeId(match.refereeId || '');
                                      await fetchReferees(match.leagueId);
                                    }}
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                  >
                                    {match.refereeId ? 'Change Ref' : 'Assign Ref'}
                                  </button>
                                )}
                              </>
                            )}
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadRoster(match.id, 'home');
                              }}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                              title="Download Home Team Roster PDF"
                            >
                              📄 Home
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadRoster(match.id, 'away');
                              }}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                              title="Download Away Team Roster PDF"
                            >
                              📄 Away
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
      ) : (
        <div>
          {sortedDates.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '20px' }}>No matches found</p>
            </div>
          ) : (
            sortedDates.map((date) => (
              <div key={date} className="card" style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2E7D32' }}>
                  {new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                  {matchesByDate[date].map((match) => (
                    <div
                      key={match.id}
                      style={{
                        padding: '15px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        backgroundColor: match.status === 'completed' ? '#E8F5E9' : '#fff',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/matches/${match.id}`)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <strong>{match.scheduledTime}</strong>
                        </div>
                        {getStatusBadge(match.status)}
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {match.homeTeam?.name || 'TBD'}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0' }}>
                          {match.status === 'completed' ? (
                            `${match.homeScore} - ${match.awayScore}`
                          ) : (
                            'vs'
                          )}
                        </div>
                        <div style={{ fontWeight: '500' }}>
                          {match.awayTeam?.name || 'TBD'}
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#757575', marginTop: '8px' }}>
                        <div>
                          📍{' '}
                          <span
                            style={{ 
                              color: '#2E7D32', 
                              cursor: 'pointer', 
                              textDecoration: 'underline',
                              fontWeight: '500'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLocationClick(match.location, match.id);
                            }}
                          >
                            {match.location}
                          </span>
                        </div>
                        {match.league && <div>🏆 {match.league.name}</div>}
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/matches/${match.id}`);
                          }}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.totalPages} (Showing {filteredMatches.length} of {pagination.total} matches)
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pagination.totalPages}
          >
            Next
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={currentPage >= pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}

      {showLocationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
            <h2 style={{ marginTop: 0 }}>Location Options</h2>
            <p style={{ color: '#757575', marginBottom: '20px' }}>
              How would you like to view the location?
            </p>
            <p style={{ color: '#9E9E9E', fontSize: '14px', marginBottom: '20px' }}>
              <strong>{selectedLocation}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                className="btn btn-primary"
                onClick={handleGoogleMaps}
                style={{ width: '100%' }}
              >
                📍 Open in Google Maps
              </button>
              {fieldId ? (
                <button
                  className="btn btn-secondary"
                  onClick={handleFieldView}
                  style={{ width: '100%' }}
                >
                  🏟️ View Field Details
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  disabled
                  style={{ width: '100%', opacity: 0.5 }}
                  title="Field information not available for this location"
                >
                  🏟️ View Field Details (Not Available)
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setShowLocationModal(false)}
                style={{ width: '100%' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Matches;

